import { IStringerBlock, IStringerGeometry, IStringerType } from '@stair-craft/common/util';

import { STRINGER_THICKNESS, STRINGER_WIDTH } from '../../constants';
import { IBoardBand, boardBand } from '../shared-board-band/board-band';
import {
    IPlanLine,
    IPlanPoint,
    planAdd,
    planIntersect,
    planLength,
    planNormalToward,
    planSub,
    planUnit,
} from '../shared-plan-geometry/plan-geometry';

/** One step of a turn zone (a winder or a landing) as the boards under its edges see it. */
export interface ITurnSupportStep {
    /** Tread top of the step. */
    readonly top: number;
    /** The leading edge — the riser line the step starts at. */
    readonly lead: IPlanLine;
    /** The trailing edge — the riser line of the next step. */
    readonly trail: IPlanLine;
    /**
     * Edges that carry a board, each a polyline from the leading to the trailing edge. The k-th
     * polyline of every step belongs to the k-th wall of the turn (outer, then inner).
     */
    readonly walls: readonly (readonly IPlanPoint[])[];
    /** Any point inside the step: boards lie on its side of the edge. */
    readonly interior: IPlanPoint;
}

export interface ITurnSupportOptions {
    readonly stringerType: IStringerType;
    readonly treadThickness: number;
    readonly riserHeight: number;
    /** Vertical depth of a kosour board under the tread line (its sloped bottom continues the flights'). */
    readonly kosourDrop: number;
    /** Bottom of the flight-1 kosour at its end: a kosour wall bottom starts there. */
    readonly entryBottom?: number;
    /** Lift of a тетива top above the riser-line corners, and its vertical depth. */
    readonly tetivaLift: number;
    readonly tetivaDrop: number;
    /** A landing's boards stay level: the bottom at the lowest point, and on a тетива the top too, so their ends match on every side. */
    readonly level?: boolean;
}

interface IChainPosition {
    readonly start: number;
    readonly total: number;
}

/** Where the band line at `depth` inward from segment a → b crosses the cut line. */
function bandCorner(a: IPlanPoint, b: IPlanPoint, normal: IPlanPoint, depth: number, cut: IPlanLine): IPlanPoint {
    const line: IPlanLine = { point: planAdd(a, normal, depth), direction: planSub(b, a) };
    return planIntersect(line, cut) ?? planAdd(cut.point, normal, depth);
}

/** The mitre line at a wall corner: through the corner, halfway between both segment normals. */
function mitreLine(corner: IPlanPoint, normalBefore: IPlanPoint, normalAfter: IPlanPoint): IPlanLine {
    return { point: corner, direction: planAdd(normalBefore, normalAfter) };
}

function wallLength(points: readonly IPlanPoint[]): number {
    let length: number = 0;
    for (let i: number = 1; i < points.length; i++) {
        length += planLength(planSub(points[i], points[i - 1]));
    }
    return length;
}

/** Heights along one wall: top and bottom of the board at a distance `s` from the turn start. */
interface IWallProfile {
    topAt(stepIndex: number, s: number): number;
    bottomAt(stepIndex: number, s: number): number;
}

/** A point on a wall unrolled flat: `s` along the wall from the turn start, `y` its height. */
interface IWallPoint {
    readonly s: number;
    readonly y: number;
}

/**
 * The bottom of a kosour wall. It runs straight from the end of the flight-1 board, under that
 * flight's last tread, to the start of the flight-2 board, so the boards meet without a step. In
 * between it also stays under the line through the notch corners — where a winder's riser rises
 * from its tread seat — `width` square below it, as under a flight (SC-CALC-095): the tops step up
 * by a riser each winder while a straight bottom rises evenly, and at the far end of a winder only
 * about half the board was left under the tread.
 */
function kosourWallBottom(
    steps: readonly ITurnSupportStep[],
    positions: readonly IChainPosition[],
    total: number,
    options: ITurnSupportOptions
): (s: number) => number {
    const start: number = steps[0].top - options.treadThickness - options.kosourDrop;
    if (options.level || total <= 0) {
        return (): number => start;
    }
    // Flight 1 ends under its last tread, a riser below the first winder; flight 2 starts `rise` above `start`.
    const entry: number = options.entryBottom ?? start - options.riserHeight;
    const exit: number = start + steps.length * options.riserHeight;
    const straight: (s: number) => number = (s: number): number => entry + ((exit - entry) * s) / total;
    const corners: IWallPoint[] = positions
        .slice(0, -1)
        .map((position: IChainPosition, i: number) => ({ s: position.start + position.total, y: steps[i].top - options.treadThickness }));
    if (corners.length === 0) {
        return straight;
    }
    const low: IWallPoint = corners[0];
    const high: IWallPoint = corners[corners.length - 1];
    const tan: number = corners.length > 1 && high.s > low.s ? (high.y - low.y) / (high.s - low.s) : (exit - entry) / total;
    const lineAt: (s: number) => number = (s: number): number => low.y + (s - low.s) * tan;
    const offset: number =
        STRINGER_WIDTH / Math.cos(Math.atan(tan)) + Math.max(...corners.map((corner: IWallPoint) => lineAt(corner.s) - corner.y));
    return (s: number): number => (s >= total ? straight(s) : Math.min(straight(s), lineAt(s) - offset));
}

function wallProfile(steps: readonly ITurnSupportStep[], wall: number, options: ITurnSupportOptions): IWallProfile {
    const positions: IChainPosition[] = [];
    let total: number = 0;
    for (const step of steps) {
        const length: number = wallLength(step.walls[wall] ?? []);
        positions.push({ start: total, total: length });
        total += length;
    }
    if (options.stringerType === 'kosour') {
        const bottom: (s: number) => number = kosourWallBottom(steps, positions, total, options);
        return {
            topAt: (stepIndex: number): number => steps[stepIndex].top - options.treadThickness,
            bottomAt: (_stepIndex: number, s: number): number => bottom(s),
        };
    }
    const topAt: (stepIndex: number, s: number) => number = (stepIndex: number, s: number): number => {
        const position: IChainPosition = positions[stepIndex];
        const fraction: number = position.total > 0 ? (s - position.start) / position.total : 0;
        return steps[stepIndex].top + options.tetivaLift + (options.level ? 0 : fraction * options.riserHeight);
    };
    const lowest: number = topAt(0, 0) - options.tetivaDrop;
    return {
        topAt,
        bottomAt: (stepIndex: number, s: number): number => (options.level ? lowest : topAt(stepIndex, s) - options.tetivaDrop),
    };
}

function stepStart(steps: readonly ITurnSupportStep[], stepIndex: number, wall: number): number {
    let start: number = 0;
    for (let i: number = 0; i < stepIndex; i++) {
        start += wallLength(steps[i].walls[wall] ?? []);
    }
    return start;
}

interface ISegmentInput {
    readonly points: readonly IPlanPoint[];
    readonly index: number;
    readonly normals: readonly IPlanPoint[];
    readonly step: ITurnSupportStep;
    readonly band: IBoardBand;
}

/** The plan outline of one wall segment's board, cut by the riser lines or the corner mitres. */
function segmentOutline(input: ISegmentInput): IPlanPoint[] {
    const { points, index, normals, step, band } = input;
    const a: IPlanPoint = points[index];
    const b: IPlanPoint = points[index + 1];
    const normal: IPlanPoint = normals[index];
    const startCut: IPlanLine = index === 0 ? step.lead : mitreLine(a, normals[index - 1], normal);
    const endCut: IPlanLine = index === points.length - 2 ? step.trail : mitreLine(b, normal, normals[index + 1]);
    return [
        bandCorner(a, b, normal, band.near, startCut),
        bandCorner(a, b, normal, band.near, endCut),
        bandCorner(a, b, normal, band.far, endCut),
        bandCorner(a, b, normal, band.far, startCut),
    ];
}

/**
 * The bottom of one block, kept on or above the floor. A turn low over the floor has boards whose
 * bottom line runs under it; such a board lies on the floor and rises from it where the line comes
 * up, so the wall bottom stays one line with no step between the blocks. The mesh extends a
 * block's slope past the segment to its mitre corners, so the low end is lifted until the lowest
 * corner just touches the floor.
 */
function blockBottom(
    bottomFrom: number,
    bottomTo: number,
    outline: readonly IPlanPoint[],
    from: IPlanPoint,
    to: IPlanPoint
): { bottomFrom: number; bottomTo: number } {
    const low: number = Math.max(0, bottomFrom);
    const high: number = Math.max(0, bottomTo);
    if (low === 0 && high === 0) {
        return { bottomFrom: 0, bottomTo: 0 };
    }
    const axis: IPlanPoint = planSub(to, from);
    const lengthSq: number = axis.x * axis.x + axis.z * axis.z;
    const params: number[] = outline.map((point: IPlanPoint) =>
        lengthSq > 0 ? ((point.x - from.x) * axis.x + (point.z - from.z) * axis.z) / lengthSq : 0
    );
    // The corner the slope runs lowest at: before `from` when the bottom rises, past `to` when it falls.
    const t: number = high >= low ? Math.min(0, ...params) : Math.max(1, ...params);
    if (low + (high - low) * t >= 0) {
        return { bottomFrom: low, bottomTo: high };
    }
    return high >= low ? { bottomFrom: (-high * t) / (1 - t), bottomTo: high } : { bottomFrom: low, bottomTo: (low * (t - 1)) / t };
}

/**
 * Boards under the edges of a turn zone: one block per wall segment of every step, so each
 * block is convex and ends exactly on a riser line or a corner mitre. A kosour block carries
 * the tread bottom flat and slopes its bottom from flight 1 to flight 2; a тетива block runs
 * its top over the riser-line corners like the flight тетивы on both sides.
 */
export function buildTurnSupports(steps: readonly ITurnSupportStep[], options: ITurnSupportOptions): IStringerGeometry[] {
    if (steps.length === 0) {
        return [];
    }
    const band: IBoardBand = boardBand(options.stringerType);
    const wallCount: number = Math.max(...steps.map((step: ITurnSupportStep) => step.walls.length));
    const boards: IStringerGeometry[] = [];
    for (let wall: number = 0; wall < wallCount; wall++) {
        const profile: IWallProfile = wallProfile(steps, wall, options);
        steps.forEach((step: ITurnSupportStep, stepIndex: number) => {
            const points: readonly IPlanPoint[] = step.walls[wall] ?? [];
            const normals: IPlanPoint[] = [];
            for (let i: number = 0; i < points.length - 1; i++) {
                normals.push(planNormalToward(points[i], points[i + 1], step.interior));
            }
            let s: number = stepStart(steps, stepIndex, wall);
            for (let index: number = 0; index < points.length - 1; index++) {
                const a: IPlanPoint = points[index];
                const b: IPlanPoint = points[index + 1];
                const length: number = planLength(planSub(b, a));
                if (length < 1e-6) {
                    continue;
                }
                const outline: IPlanPoint[] = segmentOutline({ points, index, normals, step, band });
                const block: IStringerBlock = {
                    outline,
                    from: a,
                    to: b,
                    topFrom: profile.topAt(stepIndex, s),
                    topTo: profile.topAt(stepIndex, s + length),
                    ...blockBottom(profile.bottomAt(stepIndex, s), profile.bottomAt(stepIndex, s + length), outline, a, b),
                };
                boards.push({
                    side: 'left',
                    points: [
                        { x: a.x, y: block.topFrom, z: a.z },
                        { x: b.x, y: block.topTo, z: b.z },
                    ],
                    thickness: STRINGER_THICKNESS,
                    width: STRINGER_WIDTH,
                    block,
                });
                s += length;
            }
        });
    }
    return boards;
}

/** A line through two plan points. */
export function lineThrough(a: IPlanPoint, b: IPlanPoint): IPlanLine {
    return { point: a, direction: planUnit(planSub(b, a)) };
}
